import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  Container,
  Snackbar,
  SwipeableDrawer,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { AddIcon, DeleteIcon, EditIcon, ExpandIcon } from 'src/v3/core/icons'

import * as yup from 'yup'
import { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchClasses,
  selectActiveClass,
  selectClassByID,
  selectClassesStatus,
  fetchTextContent,
  selectTextContentByClassID,
  selectTextContentStatus,
} from 'src/v3/create/create.slice'

import { useFormik, Field, FormikProvider, FieldProps } from 'formik'
import axios from 'src/utilities/http-client'
import { compose, F, T } from 'ramda'
import DangerousHtml from 'src/components/common/dangerous-html'
import Wysiwyg from 'src/components/common/wysiwyg'
import ConfirmAction from 'src/v3/components/confirm-action'
import Loading from 'src/v3/components/loading'

export default (): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()

  let classesStatus = useAppSelector(selectClassesStatus)
  let activeClass = useAppSelector(selectActiveClass)
  let classData = useAppSelector(selectClassByID(activeClass))
  let textContentStatus = useAppSelector(selectTextContentStatus)
  let textContent = useAppSelector(selectTextContentByClassID(activeClass))

  let [errorMessage, setErrorMessage] = useState<string>('')
  let [formOpen, setFormOpen] = useState<boolean>(false)
  let [saving, setSaving] = useState<boolean>(false)
  let [deleting, setDeleting] = useState<string>('')

  let form = useFormik({
    validationSchema: yup.object({
      name: yup.string().required(),
      description: yup.string().required(),
      content: yup.string().required(),
    }),
    enableReinitialize: true,
    initialValues: {
      id: '',
      name: '',
      description: '',
      content: '',
    },
    onSubmit: (values) => {
      setSaving(true)
      if (!values.id) {
        axios
          .post('/api/v2/create/text-content', {
            name: values.name,
            description: values.description,
            content: values.content,
            classID: activeClass,
          })
          .then(() => dispatch(fetchTextContent(activeClass)))
          .then(() => setFormOpen(false))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .catch((err) => {
            setErrorMessage(err?.message || 'Error Creating Content')
            dispatch(fetchTextContent(activeClass))
            setFormOpen(false)
            setSaving(false)
          })
      } else {
        axios
          .put('/api/v2/create/text-content', {
            id: values.id,
            name: values.name,
            description: values.description,
            content: values.content,
          })
          .then(() => dispatch(fetchTextContent(activeClass)))
          .then(() => setFormOpen(false))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .catch((err) => {
            setErrorMessage(err?.message || 'Error Updating Content')
            dispatch(fetchTextContent(activeClass))
            setFormOpen(false)
            setSaving(false)
          })
      }
    },
  })

  useEffect(() => {
    if (classesStatus === 'uninitialized') {
      dispatch(fetchClasses())
    }
  }, [classesStatus])

  useEffect(() => {
    if (textContentStatus === 'uninitialized' && activeClass !== 'unselected') {
      dispatch(fetchTextContent(activeClass))
    }
  }, [textContentStatus, activeClass])

  useEffect(() => {
    if (textContentStatus === 'fulfilled' && !textContent) {
      dispatch(fetchTextContent(activeClass))
    }
  }, [activeClass])

  useEffect(() => {
    if (!formOpen) {
      form.resetForm()
    }
  }, [formOpen])

  useEffect(() => {
    if (activeClass === 'unselected') {
      navigate('/create')
    }
  }, [activeClass])

  let onEdit = (id: string) => {
    let tc = textContent.find((x) => x.id === id)
    form.setFieldValue('id', tc?.id)
    form.setFieldValue('name', tc?.name)
    form.setFieldValue('description', tc?.description)
    form.setFieldValue('content', tc?.content)
    setFormOpen(true)
  }

  return (
    <>
      <Box component="main" sx={{ p: 3 }}>
        <Typography
          variant="h4"
          className="my-4 d-inline"
          style={{
            fontFamily: 'smileAndWave',
          }}
        >
          Text Content
        </Typography>
        <Typography
          className="d-inline ml-3"
          sx={{
            marginBottom: '16px',
          }}
        >
          Create text content for {classData?.name} or edit existing content
        </Typography>
      </Box>
      <Box sx={{ px: 3, mb: 2 }}>
        <Button
          sx={{ mb: 2 }}
          variant="outlined"
          size="large"
          startIcon={<AddIcon />}
          disabled={activeClass === 'unselected'}
          onClick={compose(setFormOpen, T)}
        >
          Add New Content
        </Button>
        <Box className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {textContent?.map((tc) => (
            <Box key={tc.id}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Box className="grid gap-4">
                  <Box className="px-2">
                    <Typography variant="h6">{tc.name}</Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                      {tc.description}
                    </Typography>
                  </Box>
                  <Box>
                    <div className="flex flex-row-reverse my-2">
                      <Tooltip title="Delete">
                        <Button
                          variant="outlined"
                          className="ml-3"
                          onClick={() => setDeleting(`delete-${tc.id}`)}
                        >
                          <DeleteIcon />
                        </Button>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <Button
                          variant="outlined"
                          className="ml-3"
                          onClick={() => onEdit(tc.id)}
                        >
                          <EditIcon />
                        </Button>
                      </Tooltip>
                    </div>
                  </Box>
                  <ConfirmAction
                    executeAction={() =>
                      axios
                        .delete(
                          `/api/v2/create/delete-text-content?TextContentID=${tc.id}`,
                        )
                        .then(() => dispatch(fetchTextContent(activeClass)))
                    }
                    open={deleting}
                    setOpen={setDeleting}
                    openText={`delete-${tc.id}`}
                    title="Confirm Deletion"
                    text={`Are you sure you want to delete ${tc.name}?`}
                    onFailure={(err) =>
                      setErrorMessage(
                        err.message || 'Error Deleting Text Content',
                      )
                    }
                  />
                </Box>
                <Accordion disableGutters sx={{ mt: 1 }}>
                  <AccordionSummary expandIcon={<ExpandIcon />}>
                    View Full Content
                  </AccordionSummary>
                  <AccordionDetails
                    className="px-3 pt-0 flex justify-content-between"
                    sx={{
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <DangerousHtml htmlVal={tc.content} isEncoded={true} />
                  </AccordionDetails>
                </Accordion>
              </Card>
            </Box>
          ))}
        </Box>
        <SwipeableDrawer
          anchor="bottom"
          open={formOpen && !saving}
          onOpen={compose(setFormOpen, T)}
          onClose={compose(setFormOpen, F)}
          style={{ zIndex: 2000 }}
        >
          <Container maxWidth="lg">
            <Card className="m-4 vh-full overflow-auto">
              <FormikProvider value={form}>
                <form className="p-4" onSubmit={form.handleSubmit}>
                  <TextField
                    fullWidth
                    className="mt-4"
                    variant="standard"
                    value={form.values.name}
                    label="Name"
                    name="name"
                    onChange={form.handleChange}
                    error={form.touched.name && !!form.errors.name}
                  />

                  <TextField
                    fullWidth
                    className="my-4"
                    variant="standard"
                    value={form.values.description}
                    label="Description"
                    name="description"
                    onChange={form.handleChange}
                    error={
                      form.touched.description && !!form.errors.description
                    }
                  />

                  {document && (
                    <Field name="content">
                      {({ field }: FieldProps) => (
                        <Wysiwyg
                          value={form.values.content}
                          onChange={field.onChange(field.name)}
                        />
                      )}
                    </Field>
                  )}
                  <DangerousHtml
                    htmlVal={form.values.content}
                    isEncoded={true}
                  />

                  <div className="flex flex-row-reverse">
                    <Button
                      variant="contained"
                      color="primary"
                      className="mt-4 mr-3"
                      type="submit"
                    >
                      Submit
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      className="mt-4 mr-3"
                      onClick={compose(setFormOpen, F)}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </FormikProvider>
            </Card>
          </Container>
        </SwipeableDrawer>
      </Box>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Loading entities={[classesStatus, textContentStatus]} loading={saving} />
    </>
  )
}
